import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { calculateMonthlySalaryCost } from '@/lib/salaryCalculations';

export interface CostBreakdown {
  fixed: number;
  variable: number;
  total: number;
}

export interface CostAnalysis {
  employees: CostBreakdown;
  recurring: CostBreakdown;
  total: CostBreakdown;
  breakEven: number;
  safetyMargin: number;
  operationalLeverage: number;
  currentRevenue: number;
  receivedRevenue: number;
  commissionRate: number;
  commissionCost: number;
  currentCashBalance: number;
  burnRate: number;
  cashRunwayMonths: number;
}

export function useCostAnalysis() {
  return useQuery({
    queryKey: ['cost-analysis'],
    queryFn: async (): Promise<CostAnalysis> => {
      // Fetch exchange rate
      const { data: exchangeRateData } = await supabase
        .from('exchange_rates')
        .select('rate')
        .eq('from_currency', 'BRL')
        .eq('to_currency', 'USD')
        .order('effective_date', { ascending: false })
        .limit(1)
        .single();

      const exchangeRate = exchangeRateData?.rate || 0.18;

      // Commission rate (10% sobre receita recebida)
      const commissionRate = 0.10;

      // Current month date range
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

      // Fetch employees with all salary fields
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('base_salary, salary_currency, cost_type, benefits_cost, salary_frequency, weekly_payment_type, hourly_rate, projected_hours_per_week, payment_day_of_week')
        .eq('status', 'active');

      if (empError) throw empError;

      // Fetch recurring expenses
      const { data: recurring, error: recError } = await supabase
        .from('recurring_expenses')
        .select('amount, currency, cost_type')
        .eq('is_active', true);

      if (recError) throw recError;

      // Fetch current month revenue from WON deals (for reference)
      const { data: monthlyDeals } = await supabase
        .from('deals')
        .select('total_amount, currency')
        .eq('stage', 'won')
        .gte('closed_date', firstDayOfMonth.toISOString().split('T')[0])
        .lte('closed_date', lastDayOfMonth.toISOString().split('T')[0]);

      // Fetch RECEIVED revenue from payment_transactions (confirmed payments)
      const { data: receivedPayments } = await supabase
        .from('payment_transactions')
        .select('amount, currency')
        .eq('status', 'confirmed')
        .gte('transaction_date', firstDayOfMonth.toISOString().split('T')[0])
        .lte('transaction_date', lastDayOfMonth.toISOString().split('T')[0]);

      // Calculate monthly revenue (WON deals - for reference)
      const currentRevenue = (monthlyDeals || []).reduce((sum, deal) => {
        const amount = deal.currency === 'BRL' 
          ? (deal.total_amount || 0) * exchangeRate 
          : (deal.total_amount || 0);
        return sum + amount;
      }, 0);

      // Calculate RECEIVED revenue (confirmed payments)
      const receivedRevenue = (receivedPayments || []).reduce((sum, tx) => {
        const amount = tx.currency === 'BRL' 
          ? (tx.amount || 0) * exchangeRate 
          : (tx.amount || 0);
        return sum + amount;
      }, 0);

      // Fetch bank account balances
      const { data: bankAccounts } = await supabase
        .from('bank_accounts')
        .select('current_balance, currency')
        .eq('is_active', true);

      // Calculate total cash balance in USD
      const currentCashBalance = (bankAccounts || []).reduce((sum, acc) => {
        const balance = acc.currency === 'BRL' 
          ? (acc.current_balance || 0) * exchangeRate 
          : (acc.current_balance || 0);
        return sum + balance;
      }, 0);

      // Calculate employee costs - ALL base salaries are FIXED
      const employeeCosts: CostBreakdown = {
        fixed: 0,
        variable: 0,
        total: 0,
      };

      (employees || []).forEach(emp => {
        // Calculate proper monthly cost based on frequency
        const baseMonthlyCost = calculateMonthlySalaryCost(emp, currentYear, currentMonth);
        const benefitsCost = Number(emp.benefits_cost) || 0;
        const rawCost = baseMonthlyCost + benefitsCost;
        
        const baseCost = emp.salary_currency === 'BRL' 
          ? rawCost * exchangeRate
          : rawCost;
        
        // All base salaries are FIXED costs
        employeeCosts.fixed += baseCost;
        employeeCosts.total += baseCost;
      });

      // Commission is VARIABLE cost = % of RECEIVED revenue
      const commissionCost = receivedRevenue * commissionRate;
      employeeCosts.variable = commissionCost;
      employeeCosts.total += commissionCost;

      // Calculate recurring costs by type
      const recurringCosts: CostBreakdown = {
        fixed: 0,
        variable: 0,
        total: 0,
      };

      (recurring || []).forEach(exp => {
        const cost = exp.currency === 'BRL' ? exp.amount * exchangeRate : exp.amount;
        
        if (exp.cost_type === 'fixed') {
          recurringCosts.fixed += cost;
        } else {
          recurringCosts.variable += cost;
        }
        recurringCosts.total += cost;
      });

      // Total costs
      const totalCosts: CostBreakdown = {
        fixed: employeeCosts.fixed + recurringCosts.fixed,
        variable: employeeCosts.variable + recurringCosts.variable,
        total: employeeCosts.total + recurringCosts.total,
      };

      // Burn rate = total monthly costs
      const burnRate = totalCosts.total;

      // Cash runway = how many months the company can operate
      const cashRunwayMonths = burnRate > 0 
        ? currentCashBalance / burnRate 
        : Infinity;

      // Calculate break-even using RECEIVED revenue for variable ratio
      const variableRatio = receivedRevenue > 0 ? totalCosts.variable / receivedRevenue : 0;
      const contributionMarginRatio = 1 - variableRatio;
      const breakEven = contributionMarginRatio > 0 
        ? totalCosts.fixed / contributionMarginRatio 
        : 0;

      // Safety margin = (Received Revenue - Break-even) / Received Revenue
      const safetyMargin = receivedRevenue > 0 
        ? ((receivedRevenue - breakEven) / receivedRevenue) * 100 
        : 0;

      // Operational leverage = Contribution Margin / Operating Profit
      const contributionMargin = receivedRevenue - totalCosts.variable;
      const operatingProfit = receivedRevenue - totalCosts.total;
      const operationalLeverage = operatingProfit > 0 
        ? contributionMargin / operatingProfit 
        : 0;

      return {
        employees: employeeCosts,
        recurring: recurringCosts,
        total: totalCosts,
        breakEven,
        safetyMargin,
        operationalLeverage,
        currentRevenue,
        receivedRevenue,
        commissionRate,
        commissionCost,
        currentCashBalance,
        burnRate,
        cashRunwayMonths,
      };
    },
    staleTime: 60000,
  });
}

export function useBreakEvenImpact(additionalFixedCost: number) {
  const { data: analysis } = useCostAnalysis();
  
  if (!analysis) return null;
  
  const newFixedCosts = analysis.total.fixed + additionalFixedCost;
  const variableRatio = analysis.receivedRevenue > 0 
    ? analysis.total.variable / analysis.receivedRevenue 
    : 0;
  const contributionMarginRatio = 1 - variableRatio;
  
  const newBreakEven = contributionMarginRatio > 0 
    ? newFixedCosts / contributionMarginRatio 
    : 0;
  
  return {
    currentBreakEven: analysis.breakEven,
    newBreakEven,
    increase: newBreakEven - analysis.breakEven,
    percentageIncrease: analysis.breakEven > 0 
      ? ((newBreakEven - analysis.breakEven) / analysis.breakEven) * 100 
      : 0,
  };
}
